import { createContext, useEffect, PropsWithChildren } from 'react';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import CurrentUser from 'src/types/CurrentUser';
import useAuth from 'src/api/auth/useAuth';
import useLogOut from 'src/api/auth/useLogOut';
import { useSwitchOrganization } from 'src/api/auth/useSwitchOrganization';
import { authzClient } from 'src/context/authz';
import { clearFilterMemory } from 'src/pages/authenticated/procurement/filters/procurementFilters';
import { setNotify403IsAdmin } from 'src/api/notify403';
import { rpcClient } from 'src/api/rpc/client';
import {
  clearSentryUser,
  setSentryImitating,
  setSentryUser,
} from 'src/integrations/sentry/sentry';

export interface AuthContextData {
  isFetchingAuthInitialStatus: boolean;
  currentUser?: CurrentUser;
  errorLoadingUser?: Error | unknown;
  refetchUser: (options?: RefetchOptions) => Promise<QueryObserverResult>;
  isRefetchingUser: boolean;
  logOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  isSwitchingOrganization: boolean;
  /**
   * Start view-only imitation of another user. Gently-admin only and enforced
   * server-side; reloads the page on success, resolves without reloading on
   * failure (the RPC error handling has already toasted).
   */
  startImitation: (userId: string) => Promise<void>;
  /** End imitation and return to the real admin identity. Idempotent. */
  stopImitation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/** Fade the current page to white before a hard navigation. */
function fadeOutPage(durationMs = 400): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9999',
      background: 'white',
      opacity: '0',
      transition: `opacity ${durationMs}ms ease-out`,
      pointerEvents: 'none',
    });
    document.body.appendChild(overlay);
    // Force layout so the browser registers opacity:0 before transitioning
    overlay.getBoundingClientRect();
    overlay.style.opacity = '1';
    const done = () => resolve();
    overlay.addEventListener('transitionend', done, { once: true });
    // Fallback in case transitionend never fires (reduced motion, backgrounded tab, etc.)
    setTimeout(done, durationMs + 100);
  });
}

// Both imitation transitions swap the effective identity for the whole
// session, so they take the same route as an org switch: drop the cached
// abilities, then hard-reload so every cache in the app is rebuilt against
// the new identity. Called imperatively rather than through `useMutation` —
// the MutationCache veto blocks writes while imitating, and `stop` is exactly
// the write that has to get through. Module-scope (no component state) so the
// references handed out through context stay stable across renders.
async function startImitation(userId: string): Promise<void> {
  try {
    await rpcClient.admin.imitation.start({ userId });
    authzClient.reset();
    window.location.reload();
  } catch (error) {
    // Error already surfaced by oRPC's error handling
    console.error('Failed to start imitation:', error);
  }
}

async function stopImitation(): Promise<void> {
  try {
    await rpcClient.admin.imitation.stop();
    authzClient.reset();
    window.location.reload();
  } catch (error) {
    console.error('Failed to stop imitation:', error);
  }
}

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const posthog = usePostHog();
  const {
    isFetchingAuthInitialStatus,
    currentUser,
    error: errorLoadingUser,
    isRefetching: isRefetchingUser,
    refetchUser,
  } = useAuth();

  const { mutateAsync: logOutApi } = useLogOut();
  const { mutateAsync: switchOrgApi, isPending: isSwitchingOrganization } =
    useSwitchOrganization();

  useEffect(() => {
    setNotify403IsAdmin(currentUser?.isAdmin ?? false);
  }, [currentUser?.isAdmin]);

  // Identify user in PostHog when currentUser is loaded
  useEffect(() => {
    if (currentUser?.imitation) {
      // Never identify PostHog as the imitated user: `identify` would fuse the
      // admin's anonymous session into the target's person profile and rewrite
      // their properties. Flags come from the backend instead (the `imitation`
      // block carries the target's evaluated flags).
      //
      // Sentry stays on the REAL admin — they're the one driving the session —
      // with a tag saying whose screen it was.
      setSentryUser({ id: currentUser.imitation.realUser.id });
      setSentryImitating(true);
      return;
    }

    setSentryImitating(false);

    if (currentUser && posthog) {
      // Set person properties for feature flag evaluation before reloading flags
      // https://posthog.com/docs/libraries/js#overriding-server-properties
      posthog.setPersonPropertiesForFlags({
        organizationId: currentUser.organizationId,
        ...(currentUser.tenantId && { tenantId: currentUser.tenantId }),
      });

      posthog.identify(currentUser.id, {
        email: currentUser.emails[0],
        name: currentUser.name,
        organizationId: currentUser.organizationId,
        ...(currentUser.tenantId && { tenantId: currentUser.tenantId }),
      });

      // Identify the same user in Sentry so errors are attributed to the user
      // rather than their IP address.
      setSentryUser({
        id: currentUser.id,
        email: currentUser.emails[0],
        name: currentUser.name,
      });

      // Reload feature flags with the new person properties
      posthog.reloadFeatureFlags();
    }
  }, [currentUser, posthog]);

  const logOut = async () => {
    if (posthog) {
      posthog.reset();
    }
    // Clear the Sentry user so post-logout errors aren't attributed to them
    clearSentryUser();
    setSentryImitating(false);
    // Reset authorization cache to prevent permission leakage between users
    authzClient.reset();
    // Remembered procurement filters are this user's ids.
    clearFilterMemory();
    await logOutApi();
    await refetchUser();
    await fadeOutPage();
    window.location.assign('/login');
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      await switchOrgApi({ organizationId });
      // Reset authorization cache for new org's permissions
      authzClient.reset();
      // Division and warehouse ids do not carry across organizations.
      clearFilterMemory();
      // Full page reload to clear all cached data and get fresh state
      window.location.reload();
    } catch (error) {
      // Error already handled by oRPC's error handling
      // Don't reset authz or reload on failure
      console.error('Failed to switch organization:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isFetchingAuthInitialStatus,
        currentUser,
        errorLoadingUser,
        refetchUser,
        isRefetchingUser,
        logOut,
        switchOrganization,
        isSwitchingOrganization,
        startImitation,
        stopImitation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
